/**
 * Product Page Personalizer
 * ─────────────────────────
 * Modal-basierter Editor auf der Produktseite. Aktiv nur für Produkte mit
 * konfiguriertem Tag (z.B. "personalisierbar"). Klick auf den Trigger-Button
 * öffnet ein Modal, in dem der Kunde Text / eigenes Bild aufs Motiv legt.
 *
 * Liest die aktuell auf der Produktseite gewählte Variante (Größe) aus der
 * <product-form> und nutzt sie für Druckmaße sowie für den /cart/add.js Call.
 */

(function () {
  'use strict';

  const CLOUDINARY_API_URL = (cloud) => `https://api.cloudinary.com/v1_1/${cloud}/image/upload`;

  function whenFabricReady(callback) {
    const start = Date.now();
    const tick = () => {
      if (typeof window.fabric !== 'undefined') {
        callback();
      } else if (Date.now() - start > 10000) {
        console.error('[ProductPersonalizer] Fabric.js wurde nicht geladen.');
      } else {
        setTimeout(tick, 50);
      }
    };
    tick();
  }

  function initWrapper(wrapper) {
    const config = readConfig(wrapper);
    const variants = JSON.parse(wrapper.dataset.variants || '[]');

    const els = {
      triggerBtn: wrapper.querySelector('[data-ppz-open]'),
      modal: wrapper.querySelector('[data-ppz-modal]'),
      closeBtns: wrapper.querySelectorAll('[data-ppz-close]'),
      variantTitleEl: wrapper.querySelector('[data-ppz-variant-title]'),
      variantPriceEl: wrapper.querySelector('[data-ppz-variant-price]'),
      stage: wrapper.querySelector('[data-ppz-stage]'),
      mockupImg: wrapper.querySelector('[data-ppz-mockup]'),
      canvasEl: wrapper.querySelector('[data-ppz-canvas]'),
      previewToggleBtn: wrapper.querySelector('[data-ppz-preview-toggle]'),
      addTextBtn: wrapper.querySelector('[data-ppz-add-text]'),
      finisherPanel: wrapper.querySelector('[data-ppz-finisher-panel]'),
      finisherApply: wrapper.querySelector('[data-ppz-finisher-apply]'),
      finisherFields: wrapper.querySelectorAll('[data-ppz-finisher]'),
      uploadInput: wrapper.querySelector('[data-ppz-upload]'),
      deleteBtn: wrapper.querySelector('[data-ppz-delete]'),
      undoBtn: wrapper.querySelector('[data-ppz-undo]'),
      centerBtn: wrapper.querySelector('[data-ppz-center]'),
      controls: wrapper.querySelector('[data-ppz-controls]'),
      textControlRow: wrapper.querySelector('[data-ppz-control-text]'),
      textContent: wrapper.querySelector('[data-ppz-text-content]'),
      textFont: wrapper.querySelector('[data-ppz-text-font]'),
      textColor: wrapper.querySelector('[data-ppz-text-color]'),
      textSize: wrapper.querySelector('[data-ppz-text-size]'),
      textBold: wrapper.querySelector('[data-ppz-text-bold]'),
      textItalic: wrapper.querySelector('[data-ppz-text-italic]'),
      textAlignBtns: wrapper.querySelectorAll('[data-ppz-text-align]'),
      textOutlineColor: wrapper.querySelector('[data-ppz-text-outline-color]'),
      textOutlineOff: wrapper.querySelector('[data-ppz-text-outline-off]'),
      status: wrapper.querySelector('[data-ppz-status]'),
      addToCartBtn: wrapper.querySelector('[data-ppz-add-to-cart]'),
      blankToggle: wrapper.querySelector('[data-ppz-blank-toggle]'),
      // Confirmation-Step
      editorView: wrapper.querySelector('[data-ppz-editor]'),
      confirmation: wrapper.querySelector('[data-ppz-confirmation]'),
      confirmationImg: wrapper.querySelector('[data-ppz-confirmation-img]'),
      confirmationLoading: wrapper.querySelector('[data-ppz-confirmation-loading]'),
      footerEditor: wrapper.querySelector('[data-ppz-footer-editor]'),
      footerConfirm: wrapper.querySelector('[data-ppz-footer-confirm]'),
      backToEditBtn: wrapper.querySelector('[data-ppz-back-to-edit]'),
      confirmOrderBtn: wrapper.querySelector('[data-ppz-confirm-order]'),
    };

    const state = {
      canvas: null,
      areaRect: null,
      area: null,
      currentVariant: null,
      useBlank: false,
      // Confirmation-Step: Cache der gerenderten Preview, damit sie nicht
      // zwei Mal gerendert werden muss (1x für Anzeige, 1x für Upload).
      pendingPreviewDataURL: null,
      inConfirmation: false,
    };

    // ────────────────────────────────
    // Tracking
    // ────────────────────────────────
    // Pusht GA4-kompatible Events nach window.dataLayer (GTM/GA4 sehen sie
    // automatisch) UND dispatcht parallel CustomEvent auf document — falls
    // jemand später noch ein anderes Analytics-Tool dranhängen will.
    // Funktioniert ohne Setup; wenn GA4/GTM nicht installiert ist, passieren
    // einfach keine Trackings — wird nichts kaputt gemacht.
    const track = (eventName, payload) => {
      try {
        const data = Object.assign(
          {
            event: eventName,
            personalizer_product_id: wrapper.dataset.productId,
            personalizer_product_title: wrapper.dataset.productTitle,
          },
          payload || {}
        );
        // dataLayer (GA4 / GTM)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(data);
        // CustomEvent für eigene Listener
        document.dispatchEvent(new CustomEvent('ppz:' + eventName, { detail: data }));
        if (window.console && console.debug) {
          console.debug('[ProductPersonalizer:track]', eventName, data);
        }
      } catch (e) {
        // Tracking-Fehler dürfen NIE den Editor brechen
      }
    };

    // Aktuell anzuzeigende Display-URL (Toggle-abhängig)
    const getDisplayUrl = () =>
      state.useBlank && config.mockupBlankUrl ? config.mockupBlankUrl : config.mockupUrl;

    // Aktuell zu verwendende Master-URL für Cart (Toggle-abhängig)
    const getMasterUrl = () =>
      state.useBlank && config.mockupBlankMasterUrl
        ? config.mockupBlankMasterUrl
        : config.mockupMasterUrl;

    const showStatus = (msg, isError) => {
      els.status.hidden = false;
      els.status.textContent = msg;
      els.status.classList.toggle('ppz-status-error', !!isError);
    };
    const hideStatus = () => {
      els.status.hidden = true;
      els.status.textContent = '';
      els.status.classList.remove('ppz-status-error');
    };

    // ────────────────────────────────
    // Modal Open/Close
    // ────────────────────────────────
    els.triggerBtn.addEventListener('click', openModal);
    els.closeBtns.forEach((btn) => btn.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !els.modal.hidden) closeModal();
    });

    // ────────────────────────────────
    // Blank-Toggle: tauscht das Hintergrundbild zwischen Original
    // und "Ohne Schriftzug". Die Kunden-Design-Elemente bleiben
    // auf dem Canvas erhalten — nur das Motiv darunter wechselt.
    // ────────────────────────────────
    if (els.blankToggle) {
      els.blankToggle.addEventListener('change', (e) => {
        state.useBlank = !!e.target.checked;
        const newSrc = getDisplayUrl();
        if (newSrc && els.mockupImg.src !== newSrc) {
          els.mockupImg.src = newSrc;
        }
        track('personalizer_blank_toggled', { use_blank: state.useBlank });
      });
    }

    function openModal() {
      // Aktuelle Variante vom Produkt-Form lesen, BEVOR Modal aufgeht
      updateCurrentVariant();
      if (!state.currentVariant) {
        alert('Bitte zuerst eine Variante (Größe) auswählen.');
        return;
      }

      // Modal an <body> umhängen, damit es keine Eltern-Stacking-Context-Falle gibt
      // (sonst kann ein Eltern-Element mit transform/filter das Modal "fangen" und
      // der Site-Header rendert drüber, trotz hohem z-index).
      if (els.modal.parentElement !== document.body) {
        state._modalOriginalParent = els.modal.parentElement;
        state._modalOriginalNext = els.modal.nextSibling;
        document.body.appendChild(els.modal);
      }

      els.modal.hidden = false;

      // iOS-safe Body-Scroll-Lock: Position speichern, Body fixieren
      state.savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + state.savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.classList.add('ppz-modal-open');

      // Mockup-Bild setzen — Toggle-abhängige URL
      const wantedSrc = getDisplayUrl();
      if (wantedSrc && els.mockupImg.src !== wantedSrc) {
        els.mockupImg.src = wantedSrc;
      }

      // Canvas (re-)initialisieren wenn Mockup geladen.
      // requestAnimationFrame stellt sicher, dass das Modal-Layout bereits
      // berechnet wurde — sonst liest clientWidth beim 2. Öffnen Stale-Werte
      // (Bild ist gecached → kein load-Event → sofortige sync Ausführung
      // bevor der Browser einen Layout-Pass machen konnte).
      const deferredInit = () => requestAnimationFrame(initOrResetCanvas);
      if (els.mockupImg.complete && els.mockupImg.naturalWidth > 0) {
        deferredInit();
      } else {
        els.mockupImg.addEventListener('load', deferredInit, { once: true });
      }

      hideStatus();

      track('personalizer_opened', {
        personalizer_variant_id: state.currentVariant && state.currentVariant.id,
        personalizer_variant_title: state.currentVariant && state.currentVariant.title,
      });
    }

    function closeModal() {
      els.modal.hidden = true;
      document.body.classList.remove('ppz-modal-open');

      // Confirmation-View zurücksetzen, falls Kunde dort war — sonst zeigt das
      // Modal beim nächsten Öffnen noch die alte Vorschau statt des Editors
      if (state.inConfirmation) {
        exitConfirmationView();
      }

      // Body-Scroll-Lock aufheben und ursprüngliche Scroll-Position wiederherstellen
      const restoreY = state.savedScrollY || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, restoreY);

      // Modal zurück an seinen ursprünglichen Eltern-Container hängen
      if (state._modalOriginalParent) {
        state._modalOriginalParent.insertBefore(els.modal, state._modalOriginalNext);
        state._modalOriginalParent = null;
        state._modalOriginalNext = null;
      }

      hideStatus();
      // Canvas behalten — falls Kunde nochmal aufmacht, Design bleibt
    }

    // ────────────────────────────────
    // Aktuelle Variante vom Produkt-Form lesen
    // ────────────────────────────────
    function updateCurrentVariant() {
      const variantId = getCurrentVariantIdFromForm();
      if (!variantId) {
        state.currentVariant = null;
        els.variantTitleEl.textContent = '—';
        els.variantPriceEl.textContent = '';
        return;
      }

      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (!variant) {
        state.currentVariant = null;
        return;
      }

      const sizes = parseVariantSize(variant.title);
      state.currentVariant = {
        ...variant,
        printWidthCm: (sizes && sizes.width) || config.defaultPrintWidthCm,
        printHeightCm: (sizes && sizes.height) || config.defaultPrintHeightCm,
      };

      els.variantTitleEl.textContent = variant.title;
      els.variantPriceEl.textContent = '— ' + formatPrice(variant.price);
    }

    function getCurrentVariantIdFromForm() {
      // Dawn / OS2.0: <product-form><form action="/cart/add"><input name="id" ...>
      const formInput = document.querySelector(
        'product-form form[action*="/cart/add"] [name="id"], ' +
          'form[action$="/cart/add"] [name="id"]'
      );
      if (formInput && formInput.value) return formInput.value;

      // Fallback: aktuelle URL hat ?variant=...
      const params = new URLSearchParams(window.location.search);
      if (params.get('variant')) return params.get('variant');

      // Fallback: erste verfügbare Variante
      const available = variants.find((v) => v.available);
      return available ? available.id : variants[0] ? variants[0].id : null;
    }

    // Wenn Kunde im Modal ist und außerhalb die Variante wechselt — kein Auto-Update,
    // weil Modal exklusiv ist. Beim nächsten Open wird wieder gelesen.

    // ────────────────────────────────
    // Canvas Init / Reset
    // ────────────────────────────────
    function initOrResetCanvas() {
      if (state.canvas) {
        state.canvas.dispose();
        state.canvas = null;
      }

      // Inline-Styles vom letzten Run zurücksetzen, sonst beeinflusst die alte
      // maxWidth/Height die clientWidth-Messung beim zweiten Öffnen → verzerrtes Canvas.
      els.stage.style.height = '';
      els.stage.style.maxWidth = '';

      // Backing-Store-Auflösung hochsetzen, damit Text/Edges in der Editor-Vorschau
      // gestochen scharf sind — auch auf Non-Retina-Displays oder im Theme-Editor-Viewport
      // (wo devicePixelRatio oft 1 ist).
      if (typeof fabric !== 'undefined') {
        fabric.devicePixelRatio = Math.max(2, window.devicePixelRatio || 1);
        registerDeleteControl();
      }

      // Stage-Aspect richtet sich nach der gewählten DRUCK-GRÖSSE, nicht nach dem
      // Mockup. Damit sieht der Kunde im Editor exakt das, was später gedruckt wird —
      // bei einem 30×40 cm Druck (3:4) ist die Stage entsprechend etwas breiter
      // als ein 2:3-Mockup; bei 50×70 cm (5:7) noch ein bisschen anders.
      // Das Mockup-Bild wird per CSS `object-fit: cover` zentriert in die Stage
      // gecroppt — Kunde sieht den Teil, der in die Druckgröße passt.
      let aspect;
      if (state.currentVariant && state.currentVariant.printWidthCm && state.currentVariant.printHeightCm) {
        aspect = state.currentVariant.printHeightCm / state.currentVariant.printWidthCm;
      } else {
        aspect = els.mockupImg.naturalHeight / els.mockupImg.naturalWidth;
      }

      const containerWidth = els.stage.clientWidth;
      const maxStageHeight = Math.min(window.innerHeight * 0.6, 650);

      let stageWidth = containerWidth;
      let stageHeight = stageWidth * aspect;
      if (stageHeight > maxStageHeight) {
        stageHeight = maxStageHeight;
        stageWidth = stageHeight / aspect;
        // Stage proportional schmaler machen, weiterhin zentriert via margin: 0 auto
        els.stage.style.maxWidth = stageWidth + 'px';
      } else {
        els.stage.style.maxWidth = '';
      }
      els.stage.style.height = stageHeight + 'px';
      els.canvasEl.width = stageWidth;
      els.canvasEl.height = stageHeight;

      const canvas = new fabric.Canvas(els.canvasEl, {
        selection: true,
        preserveObjectStacking: true,
        backgroundColor: 'transparent',
        enableRetinaScaling: true,
        imageSmoothingEnabled: true,
      });

      // Context-Settings für höchste Bildqualität setzen
      if (canvas.contextContainer) {
        canvas.contextContainer.imageSmoothingQuality = 'high';
      }

      const area = computePrintAreaPx(canvas, config);
      const areaRect = new fabric.Rect({
        left: area.x,
        top: area.y,
        width: area.w,
        height: area.h,
        fill: 'transparent',
        // Sehr subtiler Rahmen — Kunde soll nicht von gestrichelter Linie abgelenkt werden
        stroke: 'rgba(120, 120, 120, 0.35)',
        strokeDashArray: [3, 3],
        strokeWidth: 1,
        selectable: false,
        evented: false,
        excludeFromExport: true,
      });
      canvas.add(areaRect);

      state.canvas = canvas;
      state.areaRect = areaRect;
      state.area = area;

      bindCanvasInteractions();
    }

    function bindCanvasInteractions() {
      const canvas = state.canvas;
      const areaRect = state.areaRect;
      const area = state.area;

      const updateControlsForSelection = () => {
        const obj = canvas.getActiveObject();
        els.deleteBtn.disabled = !obj || obj === areaRect;
        if (els.centerBtn) els.centerBtn.disabled = !obj || obj === areaRect;
        if (!obj || obj === areaRect) {
          els.controls.hidden = true;
          els.textControlRow.hidden = true;
          return;
        }
        els.controls.hidden = false;
        if (obj.type === 'i-text' || obj.type === 'textbox') {
          els.textControlRow.hidden = false;
          els.textContent.value = obj.text || '';
          els.textFont.value = obj.fontFamily || 'Arial';
          els.textColor.value = obj.fill || '#000000';
          els.textSize.value = obj.fontSize || 60;
          // Bold/Italic-Toggle-Status aktualisieren
          if (els.textBold) els.textBold.classList.toggle('is-active', obj.fontWeight === 'bold');
          if (els.textItalic) els.textItalic.classList.toggle('is-active', obj.fontStyle === 'italic');
          // Alignment
          if (els.textAlignBtns) {
            els.textAlignBtns.forEach((b) =>
              b.classList.toggle('is-active', b.dataset.ppzTextAlign === (obj.textAlign || 'left'))
            );
          }
          // Outline
          if (els.textOutlineColor) {
            const hasOutline = obj.stroke && obj.strokeWidth > 0;
            if (hasOutline && obj.stroke) els.textOutlineColor.value = obj.stroke;
            if (els.textOutlineOff) {
              els.textOutlineOff.classList.toggle('is-active', !hasOutline);
            }
          }
        } else {
          els.textControlRow.hidden = true;
        }
      };
      canvas.on('selection:created', updateControlsForSelection);
      canvas.on('selection:updated', updateControlsForSelection);
      canvas.on('selection:cleared', updateControlsForSelection);

      // ────────────────────────────────────────────────────────────
      // Druckbereich-Constraint: Objekte können nicht aus dem definierten
      // Druckbereich (area) gezogen oder skaliert werden.
      //
      // Direkte Bounding-Box-Berechnung statt Fabric's getBoundingRect —
      // robuster für Text (wo Schrift-Metriken die Box manchmal verfälschen)
      // und sauberes absolutes Clamping ohne Zittern.
      // ────────────────────────────────────────────────────────────
      const constrainToArea = (obj) => {
        if (!obj || obj === areaRect) return;

        const w = obj.getScaledWidth();
        const h = obj.getScaledHeight();

        // Bounding-Box-Position (top-left) abhängig von origin
        const bbLeft = obj.originX === 'center' ? obj.left - w / 2 : obj.left;
        const bbTop = obj.originY === 'center' ? obj.top - h / 2 : obj.top;

        // Clampe Bounding-Box in den Druckbereich
        const newBbLeft = Math.max(area.x, Math.min(area.x + area.w - w, bbLeft));
        const newBbTop = Math.max(area.y, Math.min(area.y + area.h - h, bbTop));

        // Zurück zur Objekt-Position rechnen
        const newLeft = obj.originX === 'center' ? newBbLeft + w / 2 : newBbLeft;
        const newTop = obj.originY === 'center' ? newBbTop + h / 2 : newBbTop;

        if (newLeft !== obj.left || newTop !== obj.top) {
          obj.left = newLeft;
          obj.top = newTop;
          obj.setCoords();
        }
      };
      canvas.on('object:moving', (e) => constrainToArea(e.target));
      canvas.on('object:scaling', (e) => constrainToArea(e.target));
      canvas.on('object:rotating', (e) => constrainToArea(e.target));

      // ────────────────────────────────────────────────────────────
      // Snap-to-Center: beim Verschieben rastet Objekt ein, wenn sein
      // Mittelpunkt nah an Druckbereich-Mitte ist. Visuelle Guide-Linien
      // erscheinen während des Snaps (Brand-Orange).
      // ────────────────────────────────────────────────────────────
      const SNAP_THRESHOLD = 8;
      let vGuide = null;
      let hGuide = null;

      const showGuide = (which) => {
        if (which === 'v' && !vGuide) {
          vGuide = new fabric.Line(
            [area.x + area.w / 2, area.y, area.x + area.w / 2, area.y + area.h],
            { stroke: '#F4A200', strokeWidth: 1, strokeDashArray: [4, 4],
              selectable: false, evented: false, excludeFromExport: true,
              hoverCursor: 'default' }
          );
          canvas.add(vGuide);
          canvas.bringToFront(vGuide);
        }
        if (which === 'h' && !hGuide) {
          hGuide = new fabric.Line(
            [area.x, area.y + area.h / 2, area.x + area.w, area.y + area.h / 2],
            { stroke: '#F4A200', strokeWidth: 1, strokeDashArray: [4, 4],
              selectable: false, evented: false, excludeFromExport: true,
              hoverCursor: 'default' }
          );
          canvas.add(hGuide);
          canvas.bringToFront(hGuide);
        }
      };

      const hideGuides = () => {
        if (vGuide) { canvas.remove(vGuide); vGuide = null; }
        if (hGuide) { canvas.remove(hGuide); hGuide = null; }
      };

      const getObjCenter = (obj) => {
        const w = obj.getScaledWidth();
        const h = obj.getScaledHeight();
        const cx = obj.originX === 'center' ? obj.left : obj.left + w / 2;
        const cy = obj.originY === 'center' ? obj.top : obj.top + h / 2;
        return { cx, cy, w, h };
      };

      const setObjCenter = (obj, cx, cy) => {
        const w = obj.getScaledWidth();
        const h = obj.getScaledHeight();
        obj.left = obj.originX === 'center' ? cx : cx - w / 2;
        obj.top = obj.originY === 'center' ? cy : cy - h / 2;
        obj.setCoords();
      };

      canvas.on('object:moving', (e) => {
        const obj = e.target;
        if (!obj || obj === areaRect) return;
        const areaCenterX = area.x + area.w / 2;
        const areaCenterY = area.y + area.h / 2;
        const { cx, cy } = getObjCenter(obj);

        // Vertikal-Linie (horizontale Mitte)
        if (Math.abs(cx - areaCenterX) < SNAP_THRESHOLD) {
          setObjCenter(obj, areaCenterX, cy);
          showGuide('v');
        } else {
          if (vGuide) { canvas.remove(vGuide); vGuide = null; }
        }

        // Horizontal-Linie (vertikale Mitte)
        const newCx = getObjCenter(obj).cx;
        if (Math.abs(cy - areaCenterY) < SNAP_THRESHOLD) {
          setObjCenter(obj, newCx, areaCenterY);
          showGuide('h');
        } else {
          if (hGuide) { canvas.remove(hGuide); hGuide = null; }
        }
      });

      canvas.on('mouse:up', hideGuides);

      // ────────────────────────────────────────────────────────────
      // Zentrieren-Button: aktives Objekt mittig im Druckbereich
      // ────────────────────────────────────────────────────────────
      if (els.centerBtn) {
        els.centerBtn.onclick = () => {
          const obj = canvas.getActiveObject();
          if (!obj || obj === areaRect) return;
          const areaCenterX = area.x + area.w / 2;
          const areaCenterY = area.y + area.h / 2;
          setObjCenter(obj, areaCenterX, areaCenterY);
          canvas.fire('object:modified', { target: obj });
          canvas.requestRenderAll();
        };
      }

      // Gemeinsames Styling für alle interaktiven Objekte — bessere Anfasser
      const objectStyle = {
        borderColor: '#1a1a1a',
        cornerColor: '#ffffff',
        cornerStrokeColor: '#1a1a1a',
        cornerStyle: 'circle',
        cornerSize: 14,
        transparentCorners: false,
        padding: 6,
        borderScaleFactor: 1.5,
      };

      if (els.addTextBtn) {
        els.addTextBtn.onclick = () => {
          const text = new fabric.IText('Dein Text', {
            left: area.x + area.w / 2,
            top: area.y + area.h / 2,
            originX: 'center',
            originY: 'center',
            fontFamily: 'Arial',
            fontSize: 60,
            fill: '#000000',
            ...objectStyle,
          });
          canvas.add(text);
          canvas.setActiveObject(text);
          canvas.requestRenderAll();
          track('personalizer_text_added');
        };
      }

      // ────────────────────────────────────────────────────────────
      // Finisher-Daten — Event, Distanz, Zielzeit, Datum als EIN Block
      //
      // Der Kunde tippt in vorbereitete Felder statt freien Text zu
      // setzen. Daraus wird eine fabric.Textbox gebaut, die im unteren
      // Drittel des Druckbereichs sitzt und danach frei verschiebbar
      // bleibt. Zweiter Klick auf den Button ersetzt den Inhalt, statt
      // einen zweiten Block anzulegen — erkennbar an _ppzFinisher.
      // ────────────────────────────────────────────────────────────
if (els.finisherApply && els.finisherFields && els.finisherFields.length) {
        const finisherValue = (name) => {          // NICHT über wrapper suchen: openModal() hängt das Modal an <body>
          // um (Stacking-Context). Die Felder liegen dann außerhalb des
          // Wrappers, und wrapper.querySelector liefert null. els.finisherFields
          // sind feste Element-Referenzen und überleben das Umhängen.
          const f = Array.prototype.find.call(els.finisherFields, (el) =>
            el.getAttribute('data-ppz-finisher') === name
          );
          return f && f.value ? f.value.trim() : '';
        };

        const buildFinisherText = () => {
          const lines = [];
          const event = finisherValue('event');
          const middle = [finisherValue('distance'), finisherValue('time')]
            .filter(Boolean)
            .join('   ·   ');
          const date = finisherValue('date');
          if (event) lines.push(event);
          if (middle) lines.push(middle);
          if (date) lines.push(date);
          return lines.join('\n').toUpperCase();
        };

        const findFinisherBlock = () =>
          canvas.getObjects().find((o) => o._ppzFinisher === true) || null;

        const placeFinisher = () => {
          const content = buildFinisherText();
          if (!content) {
            showStatus('Trage mindestens eines der Felder aus.', true);
            return;
          }
          let block = findFinisherBlock();
          if (block) {
            block.set({ text: content });
          } else {
            block = new fabric.Textbox(content, {
              left: area.x + area.w / 2,
              top: area.y + area.h * 0.8,
              width: area.w * 0.82,
              originX: 'center',
              originY: 'center',
              fontFamily: 'Helvetica',
              fontSize: Math.max(14, Math.round(area.w * 0.042)),
              fontWeight: '600',
              charSpacing: 140,
              lineHeight: 1.5,
              textAlign: 'center',
              fill: '#111111',
              ...objectStyle,
            });
            block._ppzFinisher = true;
            canvas.add(block);
            track('personalizer_finisher_added');
          }
          canvas.setActiveObject(block);
          canvas.requestRenderAll();
          hideStatus();
        };

        els.finisherApply.onclick = placeFinisher;

        // Sobald der Block steht, tippen die Felder live mit.
        els.finisherFields.forEach((field) => {
          field.addEventListener('input', () => {
            const block = findFinisherBlock();
            if (!block) return;
            const content = buildFinisherText();
            if (!content) return;
            block.set({ text: content });
            canvas.requestRenderAll();
          });
        });
      }

      if (els.uploadInput) {
        els.uploadInput.onchange = (e) => {
          const file = e.target.files[0];
          if (!file) return;
          if (file.size > 20 * 1024 * 1024) {
            showStatus('Bild ist zu groß (max. 20 MB).', true);
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            fabric.Image.fromURL(ev.target.result, (img) => {
              const scale = Math.min(area.w / img.width, area.h / img.height) * 0.9;
              img.set({
                left: area.x + area.w / 2,
                top: area.y + area.h / 2,
                originX: 'center',
                originY: 'center',
                scaleX: scale,
                scaleY: scale,
                ...objectStyle,
              });
              img._originalDataURL = ev.target.result;
              canvas.add(img);
              canvas.setActiveObject(img);
              canvas.requestRenderAll();
              track('personalizer_image_uploaded', { file_size_bytes: file.size });
            });
          };
          reader.readAsDataURL(file);
          els.uploadInput.value = '';
        };
      }

      const updateActiveText = (prop, value) => {
        const obj = canvas.getActiveObject();
        if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;
        if (prop === 'fontSize') value = parseInt(value, 10);
        obj.set(prop, value);
        canvas.requestRenderAll();
      };
      els.textContent.oninput = (e) => updateActiveText('text', e.target.value);
      els.textFont.onchange = (e) => updateActiveText('fontFamily', e.target.value);
      els.textColor.oninput = (e) => updateActiveText('fill', e.target.value);
      els.textSize.oninput = (e) => updateActiveText('fontSize', e.target.value);

      // Bold-Toggle
      if (els.textBold) {
        els.textBold.onclick = () => {
          const obj = canvas.getActiveObject();
          if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;
          const newWeight = obj.fontWeight === 'bold' ? 'normal' : 'bold';
          obj.set('fontWeight', newWeight);
          els.textBold.classList.toggle('is-active', newWeight === 'bold');
          canvas.requestRenderAll();
        };
      }

      // Italic-Toggle
      if (els.textItalic) {
        els.textItalic.onclick = () => {
          const obj = canvas.getActiveObject();
          if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;
          const newStyle = obj.fontStyle === 'italic' ? 'normal' : 'italic';
          obj.set('fontStyle', newStyle);
          els.textItalic.classList.toggle('is-active', newStyle === 'italic');
          canvas.requestRenderAll();
        };
      }

      // Alignment-Buttons
      if (els.textAlignBtns) {
        els.textAlignBtns.forEach((btn) => {
          btn.onclick = () => {
            const obj = canvas.getActiveObject();
            if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;
            const align = btn.dataset.ppzTextAlign;
            obj.set('textAlign', align);
            els.textAlignBtns.forEach((b) =>
              b.classList.toggle('is-active', b === btn)
            );
            canvas.requestRenderAll();
          };
        });
      }

      // Outline: Color-Picker aktiviert die Kontur mit gewählter Farbe.
      // Der "Aus"-Knopf (durchgestrichener Kreis) deaktiviert die Kontur.
      const applyOutline = (color) => {
        const obj = canvas.getActiveObject();
        if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;
        if (color) {
          const strokeWidth = Math.max(1, (obj.fontSize || 60) * 0.05);
          obj.set({ stroke: color, strokeWidth: strokeWidth, paintFirst: 'stroke' });
        } else {
          obj.set({ stroke: null, strokeWidth: 0 });
        }
        if (els.textOutlineOff) {
          els.textOutlineOff.classList.toggle('is-active', !color);
        }
        canvas.requestRenderAll();
      };
      if (els.textOutlineColor) {
        els.textOutlineColor.oninput = (e) => applyOutline(e.target.value);
      }
      if (els.textOutlineOff) {
        els.textOutlineOff.onclick = () => applyOutline(null);
      }

      els.deleteBtn.onclick = () => {
        const obj = canvas.getActiveObject();
        if (!obj || obj === areaRect) return;
        canvas.remove(obj);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        updateControlsForSelection();
      };

      // ────────────────────────────────────────────────────────────
      // Undo-History — speichert Canvas-Zustände, klick auf Undo
      // stellt den vorherigen Zustand wieder her.
      // ────────────────────────────────────────────────────────────
      const history = {
        stack: [],
        max: 30,
        suppressed: false,
        push() {
          if (this.suppressed) return;
          if (this.stack.length >= this.max) this.stack.shift();
          // Bei iText muss "fontFamily" etc. mit serialisiert werden
          const json = canvas.toJSON([
            'fontFamily', 'fontWeight', 'fontStyle', 'fontSize', 'fill',
            'stroke', 'strokeWidth', 'paintFirst', 'textAlign',
            'originX', 'originY', 'excludeFromExport', '_cloudinaryUrl'
          ]);
          this.stack.push(JSON.stringify(json));
          updateUndoBtn();
        },
        undo() {
          if (this.stack.length < 2) return; // Need previous state
          this.stack.pop(); // Discard current
          const prev = this.stack[this.stack.length - 1];
          this.suppressed = true;
          canvas.loadFromJSON(prev, () => {
            // areaRect-Referenz nach Restore neu setzen
            const rect = canvas.getObjects().find((o) => o.excludeFromExport);
            if (rect) {
              state.areaRect = rect;
            }
            canvas.requestRenderAll();
            this.suppressed = false;
            updateUndoBtn();
            updateControlsForSelection();
          });
        }
      };
      const updateUndoBtn = () => {
        if (els.undoBtn) els.undoBtn.disabled = history.stack.length < 2;
      };

      // Initial-State pushen (leerer Canvas mit Print-Area-Rect)
      history.push();

      // History bei wichtigen Änderungen pushen
      canvas.on('object:added', () => history.push());
      canvas.on('object:removed', () => history.push());
      canvas.on('object:modified', () => history.push());

      if (els.undoBtn) {
        els.undoBtn.onclick = () => history.undo();
      }

      // Vorschau-Toggle: zeigt den Editor ohne Anfasser und Druckbereich-Rahmen,
      // sodass Kunde sieht wie das Ergebnis aussieht. Tasten halten oder einmal klicken.
      if (els.previewToggleBtn) {
        let previewActive = false;
        const enterPreview = () => {
          if (previewActive) return;
          previewActive = true;
          canvas.discardActiveObject();
          areaRect.set({ visible: false });
          canvas.selection = false;
          canvas.forEachObject((o) => { o.selectable = false; o.hoverCursor = 'default'; });
          canvas.requestRenderAll();
          els.previewToggleBtn.classList.add('is-active');
        };
        const exitPreview = () => {
          if (!previewActive) return;
          previewActive = false;
          areaRect.set({ visible: true });
          canvas.selection = true;
          canvas.forEachObject((o) => {
            if (o !== areaRect) {
              o.selectable = true;
              o.hoverCursor = 'move';
            }
          });
          canvas.requestRenderAll();
          els.previewToggleBtn.classList.remove('is-active');
        };
        els.previewToggleBtn.onclick = () => {
          if (previewActive) exitPreview();
          else enterPreview();
        };
      }
    }

    // ────────────────────────────────
    // Add to Cart — neuer 2-stufiger Flow:
    //   Schritt 1: addToCartBtn → showConfirmation()
    //              (Preview rendern + groß im Modal anzeigen)
    //   Schritt 2: confirmOrderBtn → finalizeOrder()
    //              (Cloudinary-Uploads, Cart-Add, Redirect)
    // ────────────────────────────────

    function enterConfirmationView() {
      state.inConfirmation = true;
      els.editorView.hidden = true;
      els.confirmation.hidden = false;
      els.footerEditor.hidden = true;
      els.footerConfirm.hidden = false;
      // Confirmation in den Viewport scrollen (Mobile)
      if (els.confirmation.scrollIntoView) {
        els.confirmation.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function exitConfirmationView() {
      state.inConfirmation = false;
      els.editorView.hidden = false;
      els.confirmation.hidden = true;
      els.footerEditor.hidden = false;
      els.footerConfirm.hidden = true;
      // Reset Confirmation-Bild für nächste Runde
      els.confirmationImg.hidden = true;
      els.confirmationImg.removeAttribute('src');
      els.confirmationLoading.hidden = false;
      els.confirmOrderBtn.disabled = true;
      state.pendingPreviewDataURL = null;
    }

    els.addToCartBtn.addEventListener('click', async () => {
      // Variante nochmal aktuell holen (falls Kunde sie zwischen Open & Add verändert hat)
      updateCurrentVariant();
      if (!state.currentVariant) {
        showStatus('Bitte zuerst eine Variante (Größe) auswählen.', true);
        return;
      }

      const designObjects = state.canvas
        ? state.canvas.getObjects().filter((o) => o !== state.areaRect)
        : [];

      // Validierung: ENTWEDER Text/Bild auf Canvas ODER Blank-Toggle aktiv —
      // sonst gibt's nichts zu bestellen.
      // Reine "Ohne Schriftzug"-Bestellung (Blank-Master ohne Zusatz) ist gültig.
      if (designObjects.length === 0 && !state.useBlank) {
        const msg = config.hasBlankVersion
          ? 'Bitte Text oder Bild hinzufügen — oder den Toggle "Ohne Schriftzug" aktivieren.'
          : 'Bitte zuerst Text oder Bild hinzufügen.';
        showStatus(msg, true);
        return;
      }

      // Confirmation-View öffnen + Preview rendern
      enterConfirmationView();
      track('personalizer_confirmation_shown', {
        n_objects: designObjects.length,
        use_blank: state.useBlank,
      });

      try {
        // areaRect kurz ausblenden, damit der gestrichelte Rahmen
        // NICHT in der Preview erscheint
        state.areaRect.set({ visible: false });
        state.canvas.requestRenderAll();
        const previewDataURL = await renderPreview(state.canvas, els.mockupImg);
        state.areaRect.set({ visible: true });
        state.canvas.requestRenderAll();

        state.pendingPreviewDataURL = previewDataURL;
        els.confirmationImg.src = previewDataURL;
        els.confirmationImg.hidden = false;
        els.confirmationLoading.hidden = true;
        els.confirmOrderBtn.disabled = false;
      } catch (err) {
        console.error('[ProductPersonalizer] Preview-Render fehlgeschlagen:', err);
        els.confirmationLoading.innerHTML =
          '<span style="color:#c00;font-size:0.85rem;">Vorschau konnte nicht erzeugt werden. Bitte zurück, ein Element verschieben und erneut versuchen.</span>';
      }
    });

    // Zurück-Button: zurück zum Editor
    if (els.backToEditBtn) {
      els.backToEditBtn.addEventListener('click', () => {
        exitConfirmationView();
        track('personalizer_back_to_edit');
      });
    }

    // Bestätigen-Button: jetzt der eigentliche Cart-Add-Workflow
    if (els.confirmOrderBtn) {
      els.confirmOrderBtn.addEventListener('click', async () => {
        if (!state.pendingPreviewDataURL || !state.currentVariant) return;
        els.confirmOrderBtn.disabled = true;
        els.backToEditBtn.disabled = true;

        try {
          if (!config.cloudName || !config.uploadPreset) {
            throw new Error('Cloudinary ist nicht konfiguriert.');
          }

          // 1) Kunden-hochgeladene Bilder zu Cloudinary uploaden, damit das
          //    Render-Tool sie später per URL nachladen kann.
          showStatus('Kunden-Bilder werden hochgeladen …');
          const canvasObjects = state.canvas
            .getObjects()
            .filter((o) => o !== state.areaRect);
          for (const obj of canvasObjects) {
            if (obj.type === 'image' && obj._originalDataURL && !obj._cloudinaryUrl) {
              const url = await uploadToCloudinary(obj._originalDataURL, config, 'customer-upload');
              obj._cloudinaryUrl = url;
            }
          }

          // 2) Preview hochladen (DataURL haben wir schon aus enterConfirmationView)
          showStatus('Vorschau wird hochgeladen …');
          const previewUrl = await uploadToCloudinary(
            state.pendingPreviewDataURL,
            config,
            'preview'
          );

          // 3) Design-JSON bauen — enthält alles, was das Render-Tool braucht
          const printPxWidth = Math.round(
            (state.currentVariant.printWidthCm / 2.54) * config.dpi
          );
          const printPxHeight = Math.round(
            (state.currentVariant.printHeightCm / 2.54) * config.dpi
          );

          const designData = {
            version: 2,
            createdAt: new Date().toISOString(),
            product: {
              id: wrapper.dataset.productId,
              title: wrapper.dataset.productTitle,
              variantId: state.currentVariant.id,
              variantTitle: state.currentVariant.title,
            },
            printSize: {
              widthCm: state.currentVariant.printWidthCm,
              heightCm: state.currentVariant.printHeightCm,
              widthPx: printPxWidth,
              heightPx: printPxHeight,
              dpi: config.dpi,
            },
            masterUrl: getMasterUrl(),
            useBlank: state.useBlank,
            canvas: {
              width: state.canvas.width,
              height: state.canvas.height,
            },
            area: {
              x: state.area.x,
              y: state.area.y,
              w: state.area.w,
              h: state.area.h,
            },
            objects: canvasObjects.map((o) => {
              const base = {
                type: o.type,
                left: o.left,
                top: o.top,
                originX: o.originX,
                originY: o.originY,
                scaleX: o.scaleX,
                scaleY: o.scaleY,
                angle: o.angle,
              };
              if (o.type === 'i-text' || o.type === 'textbox') {
                return {
                  ...base,
                  text: o.text,
                  fontFamily: o.fontFamily,
                  fontSize: o.fontSize,
                  fill: o.fill,
                  fontWeight: o.fontWeight || 'normal',
                  fontStyle: o.fontStyle || 'normal',
                  textAlign: o.textAlign || 'left',
                  stroke: o.stroke || null,
                  strokeWidth: o.strokeWidth || 0,
                };
              }
              if (o.type === 'image') {
                return {
                  ...base,
                  imageUrl: o._cloudinaryUrl || null,
                  naturalWidth: o.width,
                  naturalHeight: o.height,
                };
              }
              return base;
            }),
          };

          showStatus('Wird in den Warenkorb gelegt …');

          const properties = {
            'Design-Vorschau': previewUrl,
            _design_data: JSON.stringify(designData),
          };
          if (state.useBlank) {
            properties['Druckvariante'] = 'Ohne Schriftzug';
          }

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [
                {
                  id: state.currentVariant.id,
                  quantity: 1,
                  properties: properties,
                },
              ],
            }),
          });

          if (!res.ok) {
            const errJson = await res.json().catch(() => ({}));
            throw new Error(
              errJson.description || 'Konnte nicht in den Warenkorb gelegt werden.'
            );
          }

          track('personalizer_cart_added', {
            personalizer_variant_id: state.currentVariant.id,
            personalizer_variant_title: state.currentVariant.title,
            value: state.currentVariant.price / 100,
            use_blank: state.useBlank,
          });

          // Erfolg → zum Cart
          window.location.href = '/cart';
        } catch (err) {
          console.error('[ProductPersonalizer]', err);
          showStatus(
            'Fehler: ' + (err.message || 'Unbekannt') + ' — bitte erneut versuchen.',
            true
          );
          els.confirmOrderBtn.disabled = false;
          els.backToEditBtn.disabled = false;
          track('personalizer_cart_failed', { error_message: err.message });
        }
      });
    }
  }

  // ────────────────────────────────
  // Helper
  // ────────────────────────────────
  function readConfig(wrapper) {
    return {
      cloudName: wrapper.dataset.cloudinaryCloud || '',
      uploadPreset: wrapper.dataset.cloudinaryPreset || '',
      mockupUrl: wrapper.dataset.mockupUrl || '',
      // R2 (oder Cloudinary) URL der Druck-Master-Datei — wird NICHT im Browser gerendert,
      // sondern in der Order-JSON gespeichert, damit das lokale Render-Tool sie nutzen kann.
      mockupMasterUrl: wrapper.dataset.mockupMasterUrl || wrapper.dataset.mockupUrl || '',
      // Optional: Blank-Version (Motiv ohne Schriftzug)
      mockupBlankUrl: wrapper.dataset.mockupBlankUrl || '',
      mockupBlankMasterUrl: wrapper.dataset.mockupBlankMasterUrl || '',
      hasBlankVersion: wrapper.dataset.hasBlankVersion === 'true',
      defaultPrintWidthCm: parseFloat(wrapper.dataset.defaultPrintWidthCm) || 30,
      defaultPrintHeightCm: parseFloat(wrapper.dataset.defaultPrintHeightCm) || 40,
      dpi: parseInt(wrapper.dataset.dpi, 10) || 300,
      areaXPct: parseFloat(wrapper.dataset.areaXPct) || 0,
      areaYPct: parseFloat(wrapper.dataset.areaYPct) || 0,
      areaWPct: parseFloat(wrapper.dataset.areaWPct) || 100,
      areaHPct: parseFloat(wrapper.dataset.areaHPct) || 100,
      allowText: wrapper.dataset.allowText === 'true',
      allowImage: wrapper.dataset.allowImage === 'true',
    };
  }

  /**
   * Registriert einen Custom-Control bei Fabric, der oben rechts neben jedem
   * selektierten Objekt einen kleinen X-Knopf zum Löschen zeichnet.
   * Nur einmalig nötig (idempotent).
   */
  let deleteControlRegistered = false;
  function registerDeleteControl() {
    if (deleteControlRegistered || !fabric || !fabric.Object) return;
    deleteControlRegistered = true;

    const renderDeleteIcon = (ctx, left, top) => {
      const r = 11;
      ctx.save();
      ctx.translate(left, top);
      // Schatten
      ctx.shadowColor = 'rgba(0,0,0,0.18)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1;
      // Weißer Kreis-Hintergrund
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      // Schatten zurücksetzen für Strich
      ctx.shadowColor = 'transparent';
      // Rahmen
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // X
      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      const c = 4.5;
      ctx.beginPath();
      ctx.moveTo(-c, -c);
      ctx.lineTo(c, c);
      ctx.moveTo(c, -c);
      ctx.lineTo(-c, c);
      ctx.stroke();
      ctx.restore();
    };

    fabric.Object.prototype.controls.deleteControl = new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetX: 16,
      offsetY: -16,
      cursorStyle: 'pointer',
      mouseUpHandler: (eventData, transform) => {
        const target = transform.target;
        const canvas = target && target.canvas;
        if (!canvas) return false;
        canvas.remove(target);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        return true;
      },
      render: renderDeleteIcon,
      cornerSize: 22,
    });
  }

  function computePrintAreaPx(canvas, config) {
    return {
      x: (config.areaXPct / 100) * canvas.width,
      y: (config.areaYPct / 100) * canvas.height,
      w: (config.areaWPct / 100) * canvas.width,
      h: (config.areaHPct / 100) * canvas.height,
    };
  }

  function parseVariantSize(title) {
    if (!title) return null;
    const match = title.match(/(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)/);
    if (!match) return null;
    const width = parseFloat(match[1].replace(',', '.'));
    const height = parseFloat(match[2].replace(',', '.'));
    if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) return null;
    return { width, height };
  }

  function formatPrice(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  async function renderPrintComposite({ canvas, area, mockupHiresUrl, printPxWidth, printPxHeight }) {
    const off = document.createElement('canvas');
    off.width = printPxWidth;
    off.height = printPxHeight;
    const ctx = off.getContext('2d');

    const hiresMockup = await loadImageWithCors(mockupHiresUrl);

    const stageW = canvas.width;
    const stageH = canvas.height;
    const hiresScaleX = hiresMockup.naturalWidth / stageW;
    const hiresScaleY = hiresMockup.naturalHeight / stageH;

    const sourceX = area.x * hiresScaleX;
    const sourceY = area.y * hiresScaleY;
    const sourceW = area.w * hiresScaleX;
    const sourceH = area.h * hiresScaleY;

    ctx.drawImage(
      hiresMockup,
      sourceX, sourceY, sourceW, sourceH,
      0, 0, printPxWidth, printPxHeight
    );

    const canvasMultiplier = Math.max(printPxWidth / area.w, printPxHeight / area.h);
    const canvasDataURL = canvas.toDataURL({
      format: 'png',
      left: area.x,
      top: area.y,
      width: area.w,
      height: area.h,
      multiplier: canvasMultiplier,
    });
    const canvasImg = await loadImageDataURL(canvasDataURL);
    ctx.drawImage(canvasImg, 0, 0, printPxWidth, printPxHeight);

    return off.toDataURL('image/png');
  }

  function loadImageWithCors(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Konnte Mockup-Bild nicht laden: ' + url));
      img.src = url;
    });
  }

  function loadImageDataURL(dataURL) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Konnte Canvas-Datenbild nicht laden.'));
      img.src = dataURL;
    });
  }

  async function renderPreview(canvas, mockupImg) {
    // Höhere Preview-Auflösung damit Text/Edges scharf bleiben im Cart-Bild.
    // 1500 px lange Kante reicht für Retina-Display-Darstellung im Cart.
    const targetWidth = 1500;
    // Stage hat Print-Aspect (nicht Mockup-Aspect) — Vorschau soll auch so aussehen
    const aspect = canvas.height / canvas.width;
    const targetHeight = Math.round(targetWidth * aspect);

    const offCanvas = document.createElement('canvas');
    offCanvas.width = targetWidth;
    offCanvas.height = targetHeight;
    const ctx = offCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Mockup mit object-fit:cover-Logik einzeichnen (zentriert gecroppt aufs Print-Aspect)
    await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const mockupAspect = img.naturalHeight / img.naturalWidth;
        let sx, sy, sw, sh;
        if (mockupAspect > aspect) {
          // Mockup ist höher als Print-Aspect → oben/unten croppen
          sw = img.naturalWidth;
          sh = sw * aspect;
          sx = 0;
          sy = (img.naturalHeight - sh) / 2;
        } else if (mockupAspect < aspect) {
          // Mockup ist breiter → seitlich croppen
          sh = img.naturalHeight;
          sw = sh / aspect;
          sx = (img.naturalWidth - sw) / 2;
          sy = 0;
        } else {
          sx = 0; sy = 0;
          sw = img.naturalWidth;
          sh = img.naturalHeight;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
        resolve();
      };
      img.onerror = resolve;
      img.src = mockupImg.src;
    });

    // Canvas-Inhalt direkt in Ziel-Auflösung rendern (multiplier passt zur Größe)
    // → kein nachträgliches Hochskalieren → Text bleibt gestochen scharf
    const canvasMultiplier = targetWidth / canvas.width;
    const canvasDataURL = canvas.toDataURL({ format: 'png', multiplier: canvasMultiplier });
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        resolve();
      };
      img.onerror = resolve;
      img.src = canvasDataURL;
    });

    // JPEG Quality 92 — sieht aus wie PNG, aber Datei ist ~5x kleiner (~200–500 KB)
    return offCanvas.toDataURL('image/jpeg', 0.92);
  }

  async function uploadToCloudinary(dataURL, config, tag) {
    const blob = dataURLtoBlob(dataURL);
    const formData = new FormData();
    formData.append('file', blob);
    formData.append('upload_preset', config.uploadPreset);
    if (tag) formData.append('tags', `personalizer,${tag}`);
    formData.append('folder', 'product-customizer');

    const res = await fetch(CLOUDINARY_API_URL(config.cloudName), {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Cloudinary-Upload fehlgeschlagen: ${res.status} ${errText.substring(0, 200)}`);
    }
    const json = await res.json();
    if (!json.secure_url) throw new Error('Cloudinary lieferte keine URL zurück.');
    return json.secure_url;
  }

  function dataURLtoBlob(dataURL) {
    const parts = dataURL.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(parts[1]);
    const len = binary.length;
    const buf = new Uint8Array(len);
    for (let i = 0; i < len; i++) buf[i] = binary.charCodeAt(i);
    return new Blob([buf], { type: mime });
  }

  // ────────────────────────────────
  // Boot
  // ────────────────────────────────
  function bootAll() {
    document.querySelectorAll('.ppz-wrapper').forEach((wrapper) => {
      if (wrapper.dataset.ppzInited === '1') return;
      wrapper.dataset.ppzInited = '1';
      whenFabricReady(() => initWrapper(wrapper));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', bootAll);
})();